"use client";

import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type HealthcareProvidersBlok = SbBlokData & {
  title?: string;
  description?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  bg_color?: unknown;
  reverse_order?: boolean;
};

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function HealthcareProviders({ blok }: { blok: HealthcareProvidersBlok }) {
  const region = regionFromPathname(usePathname());
  const link = resolveLink(blok.button_link, region);
  const title = blok.title || "Approved healthcare provider";
  const buttonLabel = blok.button_label || "Learn more";
  const { bg } = resolveBg(blok.bg_color, "dusk-01");
  const logosOrder = blok.reverse_order ? "lg:order-1" : "lg:order-2";
  const textOrder = blok.reverse_order ? "lg:order-2" : "lg:order-1";

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-10 lg:py-[55px]">
        <div className="rounded-[12px] bg-white px-5 py-7 lg:rounded-[20px] lg:px-10 lg:py-11">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-10 xl:gap-[104px]">
            <div
              className={`order-first flex w-full flex-wrap items-center justify-center gap-x-7 gap-y-4 lg:w-auto lg:shrink-0 lg:flex-nowrap lg:gap-x-8 ${logosOrder}`}
            >
              {/* eslint-disable @next/next/no-img-element */}
              <img src="/assets/logo/vhi.png" alt="Vhi" className="h-[42px] w-auto lg:h-[51px]" />
              <img
                src="/assets/logo/ilh.png"
                alt="Irish Life Health"
                className="h-[63px] w-auto lg:h-[76px]"
              />
              <img
                src="/assets/logo/laya.png"
                alt="Laya Healthcare"
                className="h-[50px] w-auto lg:h-[60px]"
              />
              {/* eslint-enable @next/next/no-img-element */}
            </div>

            <div className={`flex flex-col gap-3 lg:flex-1 lg:gap-8 ${textOrder}`}>
              <div className="text-purple-05 flex flex-col gap-3 lg:gap-5">
                <h2 className="text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold">{title}</h2>
                {blok.description && (
                  <p className="text-body leading-[1.56] text-[#595959]">{blok.description}</p>
                )}
              </div>
              {link.href !== "#" && (
                <a
                  href={link.href}
                  {...(link.target ? { target: link.target } : {})}
                  rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                  className="text-body text-purple-04 inline-flex w-fit items-center gap-[7px] py-2.5 leading-[1.054] font-extrabold hover:underline"
                >
                  {buttonLabel}
                  <ArrowIcon />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
